import { formatInTimeZone } from "date-fns-tz";
import { Slot, SlotsStatus } from "../../types/booking";
import { HALIFAX_TZ, formatSlotTime } from "../../lib/timezone";

interface SlotPickerProps {
  slots: Slot[];
  selectedSlot: Slot | null;
  status: SlotsStatus;
  hasDate: boolean;
  onSelect: (slot: Slot) => void;
  onRetry: () => void;
  error?: boolean;
}

const GROUPS: { label: string; match: (hour: number) => boolean }[] = [
  { label: "Morning", match: (h) => h < 12 },
  { label: "Afternoon", match: (h) => h >= 12 && h < 17 },
  { label: "Evening", match: (h) => h >= 17 },
];

function halifaxHour(iso: string): number {
  return parseInt(formatInTimeZone(new Date(iso), HALIFAX_TZ, "H"), 10);
}

export function SlotPicker({
  slots,
  selectedSlot,
  status,
  hasDate,
  onSelect,
  onRetry,
  error,
}: SlotPickerProps) {
  if (!hasDate) {
    return (
      <div className="rounded-xl border border-dashed border-[#E3C9C8] bg-[#FBEFEF]/40 px-4 py-6 text-center text-sm text-[#9A6B69]">
        Pick a date above to see available times.
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div
        className="flex flex-wrap gap-2"
        role="status"
        aria-live="polite"
        aria-label="Checking availability"
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="h-10 w-24 animate-pulse rounded-full bg-[#F1DEDD]"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
        <span className="sr-only">Checking availability…</span>
      </div>
    );
  }

  if (status === "error" || error) {
    return (
      <div
        className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-5 text-center"
        role="alert"
      >
        <p className="text-sm text-rose-700">
          We couldn't load available times just now.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-full bg-[#B87775] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#a96764]"
        >
          Try again
        </button>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="rounded-xl border border-[#E3C9C8] bg-[#FBEFEF]/40 px-4 py-6 text-center text-sm text-[#9A6B69]">
        No openings on this date. Please choose another day.
      </div>
    );
  }

  return (
    <div className="space-y-5" role="radiogroup" aria-label="Available times">
      {GROUPS.map(({ label, match }) => {
        const groupSlots = slots.filter((slot) => match(halifaxHour(slot.start)));
        if (groupSlots.length === 0) return null;

        return (
          <div key={label}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#B87775]">
              {label}
            </p>
            <div className="flex flex-wrap gap-2">
              {groupSlots.map((slot) => {
                const isSelected = selectedSlot?.start === slot.start;
                const label12 = formatInTimeZone(
                  new Date(slot.start),
                  HALIFAX_TZ,
                  "h:mm a"
                );
                return (
                  <button
                    key={slot.start}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={formatSlotTime(slot.start)}
                    onClick={() => onSelect(slot)}
                    className={`min-w-[5.5rem] rounded-full border px-4 py-2 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-1 ${
                      isSelected
                        ? "border-[#B87775] bg-[#B87775] text-white shadow-sm"
                        : "border-[#E3C9C8] bg-white text-gray-700 hover:border-[#B87775] hover:bg-[#FBEFEF]"
                    }`}
                  >
                    {label12}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
